import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Platform, TouchableOpacity} from 'react-native';
import {
  Appbar,
  Text,
  TextInput,
  Button,
  Snackbar,
  Menu,
  ActivityIndicator,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';
import {useExpenseStore} from '../../store/useExpenseStore';
import {useExpenseCategoryStore} from '../../store/useExpenseCategoryStore';
import {workerApi} from '../../services/api/workerApi';
import {colors, spacing} from '../../theme';

const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE'];

const ExpenseSearchFiltersScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {searchExpenses} = useExpenseStore();
  const {expenseCategories, fetchExpenseCategories} = useExpenseCategoryStore();

  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [expenseCategoryId, setExpenseCategoryId] = useState(null);
  const [amountFrom, setAmountFrom] = useState('');
  const [amountTo, setAmountTo] = useState('');
  const [paymentMode, setPaymentMode] = useState(null);
  const [staffId, setStaffId] = useState(null);

  const [showDateFromPicker, setShowDateFromPicker] = useState(false);
  const [showDateToPicker, setShowDateToPicker] = useState(false);
  const [categoryMenuVisible, setCategoryMenuVisible] = useState(false);
  const [modeMenuVisible, setModeMenuVisible] = useState(false);
  const [staffMenuVisible, setStaffMenuVisible] = useState(false);

  const [workers, setWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [searching, setSearching] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchExpenseCategories();
    loadWorkers();
  }, [fetchExpenseCategories]);

  const loadWorkers = async () => {
    setLoadingWorkers(true);
    try {
      const response = await workerApi.list();
      const wd = response.data.data;
      setWorkers(wd?.content || wd || []);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setLoadingWorkers(false);
    }
  };

  const formatDate = d =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      : '';

  const selectedCategoryName =
    expenseCategories.find(c => c.id === expenseCategoryId)?.name || '';

  const selectedStaffName =
    workers.find(w => w.id === staffId)?.fullName || '';

  const handleSearch = useCallback(async () => {
    setSearching(true);
    try {
      const payload = {};
      if (dateFrom) payload.dateFrom = formatDate(dateFrom);
      if (dateTo) payload.dateTo = formatDate(dateTo);
      if (expenseCategoryId) payload.expenseCategoryId = expenseCategoryId;
      if (amountFrom) payload.amountFrom = parseFloat(amountFrom);
      if (amountTo) payload.amountTo = parseFloat(amountTo);
      if (paymentMode) payload.paymentMode = paymentMode;
      if (staffId) payload.staffId = staffId;

      await searchExpenses(payload);
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setSearching(false);
    }
  }, [
    dateFrom,
    dateTo,
    expenseCategoryId,
    amountFrom,
    amountTo,
    paymentMode,
    staffId,
    searchExpenses,
    navigation,
    t,
  ]);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
        />
        <Appbar.Content
          title={t('expense.searchFilters')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
        {/* Expense Date Range */}
        <Text style={styles.sectionLabel}>{t('expense.expenseDateRange')}</Text>
        <View style={styles.rowFields}>
          <View style={styles.rowField}>
            <TouchableOpacity activeOpacity={1} onPress={() => setShowDateFromPicker(true)}>
              <TextInput
                label={t('report.dateFrom')}
                value={formatDate(dateFrom)}
                mode="outlined"
                style={styles.rowInput}
                editable={false}
                right={<TextInput.Icon icon="calendar" onPress={() => setShowDateFromPicker(true)} />}
              />
            </TouchableOpacity>
          </View>
          <View style={styles.rowField}>
            <TouchableOpacity activeOpacity={1} onPress={() => setShowDateToPicker(true)}>
              <TextInput
                label={t('report.dateTo')}
                value={formatDate(dateTo)}
                mode="outlined"
                style={styles.rowInput}
                editable={false}
                right={<TextInput.Icon icon="calendar" onPress={() => setShowDateToPicker(true)} />}
              />
            </TouchableOpacity>
          </View>
        </View>
        {showDateFromPicker && (
          <DateTimePicker
            value={dateFrom || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowDateFromPicker(false);
              if (selectedDate) setDateFrom(selectedDate);
            }}
          />
        )}
        {showDateToPicker && (
          <DateTimePicker
            value={dateTo || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowDateToPicker(false);
              if (selectedDate) setDateTo(selectedDate);
            }}
          />
        )}

        {/* Category dropdown */}
        <Menu
          visible={categoryMenuVisible}
          onDismiss={() => setCategoryMenuVisible(false)}
          anchor={
            <TextInput
              label={t('expense.category')}
              value={selectedCategoryName}
              mode="outlined"
              style={styles.input}
              editable={false}
              right={<TextInput.Icon icon="chevron-down" />}
              onPressIn={() => setCategoryMenuVisible(true)}
            />
          }>
          <Menu.Item
            title={t('expense.duration.all')}
            onPress={() => {
              setExpenseCategoryId(null);
              setCategoryMenuVisible(false);
            }}
          />
          {expenseCategories.map(cat => (
            <Menu.Item
              key={cat.id}
              title={cat.name}
              onPress={() => {
                setExpenseCategoryId(cat.id);
                setCategoryMenuVisible(false);
              }}
            />
          ))}
        </Menu>

        {/* Expense Amount Range */}
        <Text style={styles.sectionLabel}>{t('expense.expenseAmountRange')}</Text>
        <View style={styles.rowFields}>
          <View style={styles.rowField}>
            <TextInput
              label={t('report.dateFrom')}
              value={amountFrom}
              onChangeText={setAmountFrom}
              mode="outlined"
              keyboardType="numeric"
              style={styles.rowInput}
            />
          </View>
          <View style={styles.rowField}>
            <TextInput
              label={t('report.dateTo')}
              value={amountTo}
              onChangeText={setAmountTo}
              mode="outlined"
              keyboardType="numeric"
              style={styles.rowInput}
            />
          </View>
        </View>

        {/* Payment Mode dropdown */}
        <Menu
          visible={modeMenuVisible}
          onDismiss={() => setModeMenuVisible(false)}
          anchor={
            <TextInput
              label={t('expense.mode')}
              value={paymentMode ? paymentMode.replace(/_/g, ' ') : ''}
              mode="outlined"
              style={styles.input}
              editable={false}
              right={<TextInput.Icon icon="chevron-down" />}
              onPressIn={() => setModeMenuVisible(true)}
            />
          }>
          <Menu.Item
            title={t('expense.duration.all')}
            onPress={() => {
              setPaymentMode(null);
              setModeMenuVisible(false);
            }}
          />
          {PAYMENT_MODES.map(mode => (
            <Menu.Item
              key={mode}
              title={mode.replace(/_/g, ' ')}
              onPress={() => {
                setPaymentMode(mode);
                setModeMenuVisible(false);
              }}
            />
          ))}
        </Menu>

        {/* Staff dropdown */}
        <Menu
          visible={staffMenuVisible}
          onDismiss={() => setStaffMenuVisible(false)}
          anchor={
            <TextInput
              label={t('expense.staff')}
              value={selectedStaffName}
              mode="outlined"
              style={styles.input}
              editable={false}
              right={
                loadingWorkers ? (
                  <TextInput.Icon icon={() => <ActivityIndicator size={16} />} />
                ) : (
                  <TextInput.Icon icon="chevron-down" />
                )
              }
              onPressIn={() => setStaffMenuVisible(true)}
            />
          }>
          <Menu.Item
            title={t('expense.duration.all')}
            onPress={() => {
              setStaffId(null);
              setStaffMenuVisible(false);
            }}
          />
          {workers.map(w => (
            <Menu.Item
              key={w.id}
              title={w.fullName}
              onPress={() => {
                setStaffId(w.id);
                setStaffMenuVisible(false);
              }}
            />
          ))}
        </Menu>

        <Button
          mode="contained"
          onPress={handleSearch}
          loading={searching}
          disabled={searching}
          style={styles.searchButton}
          contentStyle={styles.searchButtonContent}>
          {t('common.search')}
        </Button>
      </ScrollView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  form: {flex: 1, padding: spacing.md},
  input: {
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
  },
  sectionLabel: {
    fontSize: 13,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
    marginTop: spacing.xs,
  },
  rowFields: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  rowField: {
    flex: 1,
  },
  rowInput: {
    backgroundColor: colors.surface,
  },
  searchButton: {
    marginTop: spacing.md,
    marginBottom: spacing.xxl,
    backgroundColor: colors.primary,
  },
  searchButtonContent: {
    paddingVertical: spacing.xs,
  },
});

export default ExpenseSearchFiltersScreen;
